import React, { useEffect, useState } from "react";
import { ref, onValue, set } from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { db, storage } from "../../firebaseConfig";

const ComprovantesVenda = () => {
  const [imoveis, setImoveis] = useState([]);
  const [corretores, setCorretores] = useState([]);
  const [comprovantes, setComprovantes] = useState({});
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [file, setFile] = useState(null);
  const [dataVenda, setDataVenda] = useState("");
  const [corretor, setCorretor] = useState("");
  const [removerDialogOpen, setRemoverDialogOpen] = useState(false);
  const [comprovanteSelecionado, setComprovanteSelecionado] = useState(null);
  const [saving, setSaving] = useState(false);

  // 🔔 Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  /* ===================== REMOVER ===================== */
  const handleRemoverComprovante = async () => {
    const { id, fileName } = comprovanteSelecionado || {};

    if (!id || !fileName) {
      showToast("Erro: dados inválidos para remoção.", "error");
      return;
    }

    try {
      const fileRef = storageRef(storage, `comprovantesVenda/${id}/${fileName}`);
      await deleteObject(fileRef);
      await set(ref(db, `comprovantesVenda/${id}`), null);
      showToast("Comprovante removido com sucesso.", "info");
    } catch (error) {
      console.error("Erro ao remover comprovante:", error);
      showToast("Erro ao remover comprovante.", "error");
    }

    setRemoverDialogOpen(false);
    setComprovanteSelecionado(null);
  };

  /* ===================== LOAD DATA ===================== */
  useEffect(() => {
    const imoveisRef = ref(db, "imoveis");
    onValue(imoveisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const lista = Object.entries(data)
          .map(([id, item]) => ({ id, ...item }))
          .filter((item) => item.imovelInfo?.modalidade === "Venda");
        setImoveis(lista);
      }
    });

    const corretoresRef = ref(db, "corretores");
    onValue(corretoresRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const lista = Object.entries(data).map(([id, item]) => ({
          id,
          ...item,
        }));
        setCorretores(lista);
      }
    });

    const comprovantesRef = ref(db, "comprovantesVenda");
    onValue(comprovantesRef, (snapshot) => {
      const data = snapshot.val() || {};
      setComprovantes(data);
    });
  }, []);

  /* ===================== SEARCH ===================== */
  const handleSearch = (item) => {
    const termo = search.toLowerCase();
    return (
      item.imovelInfo?.codigo?.toLowerCase().includes(termo) ||
      item.imovelInfo?.proprietario?.toLowerCase().includes(termo)
    );
  };

  /* ===================== MODAL UPLOAD ===================== */
  const openModal = (imovel) => {
    setSelectedImovel(imovel);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedImovel(null);
    setFile(null);
    setDataVenda("");
    setCorretor("");
  };

  const handleUpload = async () => {
    if (!file || !dataVenda || !corretor) {
      showToast("Preencha todos os campos antes de salvar.", "warning");
      return;
    }

    setSaving(true);

    try {
      const filePath = `comprovantesVenda/${selectedImovel.id}/${file.name}`;
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const dados = {
        imovelID: selectedImovel.id,
        nomeImovel: selectedImovel.imovelInfo?.titulo || "Sem Título",
        data: dataVenda,
        corretor,
        urlPDF: url,
        fileName: file.name,
      };

      await set(ref(db, `comprovantesVenda/${selectedImovel.id}`), dados);
      showToast("Comprovante salvo com sucesso!", "success");
      handleClose();
    } catch (error) {
      console.error("Erro ao salvar comprovante:", error);
      showToast("Erro ao salvar comprovante.", "error");
    }

    setSaving(false);
  };

  /* ===================== RENDER ===================== */
  return (
    <div style={{ padding: "20px", marginLeft: "15rem" }} className="containerComprov">
      <h2>Comprovantes de Venda</h2>

      <TextField
        label="Buscar por código ou proprietário"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: "20px" }}
      />

      {imoveis.filter(handleSearch).map((imovel) => {
        const comprovante = comprovantes[imovel.id];
        return (
          <div
            key={imovel.id}
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>{imovel.imovelInfo?.titulo}</strong> -{" "}
              <span>{imovel.imovelInfo?.codigo}</span> <br />
              <small>{imovel.imovelInfo?.proprietario}</small>
            </div>

            <div>
              {comprovante ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(comprovante.urlPDF, "_blank")}
                    sx={{
                      marginRight: "10px",
                      borderColor: "var(--verde)",
                      color: "var(--verde)",
                    }}
                  >
                    Ver Comprovante
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      setComprovanteSelecionado({
                        id: imovel.id,
                        fileName: comprovante.fileName,
                      });
                      setRemoverDialogOpen(true);
                    }}
                  >
                    Remover
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => openModal(imovel)}
                  sx={{
                    backgroundColor: "var(--verde)",
                    "&:hover": { backgroundColor: "var(--bege)" },
                  }}
                >
                  Enviar Comprovante
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal de confirmação de remoção */}
      <Dialog
        open={removerDialogOpen}
        onClose={() => setRemoverDialogOpen(false)}
      >
        <DialogTitle>Confirmar Remoção</DialogTitle>
        <DialogContent>
          <p>Tem certeza que deseja remover o comprovante?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoverDialogOpen(false)} sx={{ color: "var(--verde)" }}>
            Cancelar
          </Button>
          <Button color="error" onClick={handleRemoverComprovante}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de envio */}
      <Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Enviar Comprovante</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ marginTop: "10px" }}
          />
          <TextField
            label="Data da Venda"
            type="date"
            fullWidth
            value={dataVenda}
            onChange={(e) => setDataVenda(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: "20px" }}
          />
          <TextField
            label="Corretor"
            select
            fullWidth
            value={corretor}
            onChange={(e) => setCorretor(e.target.value)}
            style={{ marginTop: "20px" }}
          >
            {corretores.map((c) => (
              <MenuItem key={c.id} value={c.nome}>
                {c.nome}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ color: "var(--verde)" }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={saving}
            sx={{
              backgroundColor: "var(--verde)",
              "&:hover": { backgroundColor: "var(--bege)" },
              minWidth: 120,
            }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: "bold", backgroundColor: "var(--verde)" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ComprovantesVenda;
